import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductPage/ProductCard';
import Header from '../components/Secciones/Header';
import RelatedProducts from '../components/ProductPage/RelatedProducts';
import Footer from '../components/Secciones/Footer';
import ProductSeo from '../components/ProductPage/ProductSeo';
import { recordProductView } from '../utils/productViewHistory';
import { PRODUCT_FEATURE_ICON_MAP, parseProductFeatures } from '../components/ProductPage/productFeatureIcons';
import { parseProductDetailRows, parseProductSellingPoints } from '../utils/productDetailRows';
import { getProductPath } from '../utils/productUrls';
import './PublicProductDetails.css';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';

const getDocumentUrl = (value) => /^https?:\/\//i.test(value)
  ? value
  : `${API_BASE}/data/${String(value).replace(/^\/+/, '')}`;

function PublicProductDetails() {
  const { product_name } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const decodedProductName = decodeURIComponent(product_name);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const searchQuery = location.state?.searchQuery || '';

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/products/${encodeURIComponent(decodedProductName)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || `Error HTTP: ${res.status}`);
        }
        const data = await res.json();
        if (data.published !== 'Yes') {
          throw new Error('Product not available');
        }
        recordProductView(data);
        setProduct(data);
        setError(null);
        const readablePath = getProductPath(data);
        if (location.pathname.replace(/\/$/, '') !== readablePath) {
          navigate(readablePath, { replace: true, state: location.state });
        }
      } catch (err) {
        console.error('Error obtaining the product:', err.message);
        setError(`Product not available: ${err.message}. Por favor, intenta de nuevo.`);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [decodedProductName, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!error) return undefined;
    const robots = document.head.querySelector('meta[name="robots"]');
    const previousRobots = robots?.getAttribute('content');
    const previousTitle = document.title;
    if (robots) robots.setAttribute('content', 'noindex, follow');
    document.title = 'Product Not Found | Valley International Supply';

    return () => {
      if (robots && previousRobots) robots.setAttribute('content', previousRobots);
      document.title = previousTitle;
    };
  }, [error]);

  if (loading) {
    return (
      <div>
        <Header />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #0000FF', borderTop: '4px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <p style={{ marginLeft: '16px', fontSize: '18px', color: '#666' }}>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Header />
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <p style={{ color: '#FF0000', fontSize: '18px' }}>{error}</p>
          <Link
            to="/"
            style={{ marginTop: '16px', display: 'inline-block', backgroundColor: '#0000FF', color: '#FFF', padding: '8px 16px', textDecoration: 'none', borderRadius: '4px' }}
          >
            Back Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div>
        <Header />
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <p style={{ color: '#FF0000', fontSize: '18px' }}>Product not found</p>
          <Link
            to="/"
            style={{ marginTop: '16px', display: 'inline-block', backgroundColor: '#0000FF', color: '#FFF', padding: '8px 16px', textDecoration: 'none', borderRadius: '4px' }}
          >
            Back Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const documents = [product.product_pdf, product.product_pdf2, product.product_pdf3]
    .filter((value) => value && !['none', 'null'].includes(String(value).toLowerCase()));
  const productFeatures = parseProductFeatures(product.product_features)
    .filter((feature) => feature?.label && PRODUCT_FEATURE_ICON_MAP[feature.icon]);
  const productSpecifications = parseProductDetailRows(product.product_specifications);
  const productKeySellingPoints = parseProductSellingPoints(product.product_key_selling_points);
  const categorySearch = `/search?q=*&category=${encodeURIComponent(product.product_category || '')}&page=1`;
  const brandSearch = `/search?q=*&brand=${encodeURIComponent(product.product_brand || '')}&page=1`;

  return (
    <div>
      <ProductSeo product={product} />
      <Header />
      <main className="pd-page">
        <nav className="pd-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link><span>/</span>
          <Link to="/search?q=*&page=1">Products</Link><span>/</span>
          {product.product_category && <><Link to={categorySearch}>{product.product_category}</Link><span>/</span></>}
          <span aria-current="page">{product.product_name}</span>
        </nav>
        <ProductCard
          productId={product.idproducts}
          name={product.product_name}
          brand={product.product_brand}
          price={parseFloat(product.product_price || 0)}
          description={product.product_description || 'Description Not Available'}
          stock={Number(product.product_stock) || 0}
          imageUrl={product.product_image || null}
          imageUrls={product.product_images}
        />
        {productFeatures.length > 0 && (
          <section className="pd-features" aria-label="Product functions">
            {productFeatures.map((feature, index) => {
              const FeatureIcon = PRODUCT_FEATURE_ICON_MAP[feature.icon];
              return (
                <div className="pd-feature" key={`${feature.icon}-${index}`}>
                  <span className="pd-feature__icon" aria-hidden="true"><FeatureIcon /></span>
                  <span>{feature.label}</span>
                </div>
              );
            })}
          </section>
        )}
        <section className="pd-details" aria-labelledby="product-details-heading">
          <h2 id="product-details-heading">Product details</h2>
          <dl className="pd-specs">
            <div><dt>Brand</dt><dd><Link to={brandSearch}>{product.product_brand || 'Not specified'}</Link></dd></div>
            <div><dt>Category</dt><dd><Link to={categorySearch}>{product.product_category || 'Industrial products'}</Link></dd></div>
            <div><dt>Availability</dt><dd>{Number(product.product_stock) > 0 ? `${product.product_stock} in stock` : 'Available on request'}</dd></div>
          </dl>
        </section>
        {productSpecifications.length > 0 && (
          <section className="pd-detail-table-section" aria-labelledby="product-specifications-heading">
            <h2 id="product-specifications-heading">Specifications</h2>
            <dl className="pd-detail-table">
              {productSpecifications.map((row, index) => <div key={`${row.title}-${index}`}><dt>{row.title}</dt><dd>{row.component}</dd></div>)}
            </dl>
          </section>
        )}
        {productKeySellingPoints.length > 0 && (
          <section className="pd-detail-table-section" aria-labelledby="product-selling-points-heading">
            <h2 id="product-selling-points-heading">Key selling points</h2>
            <ul className="pd-selling-points">
              {productKeySellingPoints.map((point, index) => <li key={`${point}-${index}`}>{point}</li>)}
            </ul>
          </section>
        )}
        {documents.length > 0 && (
          <section className="pd-documents" aria-labelledby="product-documents-heading">
            <h2 id="product-documents-heading">Technical documents</h2>
            <div className="pd-document-links">
              {documents.map((document, index) => (
                <a key={document} href={getDocumentUrl(document)} target="_blank" rel="noopener noreferrer">
                  <i className="fas fa-file-pdf" aria-hidden="true" /> Technical document {index + 1}
                </a>
              ))}
            </div>
          </section>
        )}
        <Link className="pd-back-link" to={`/search${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : '?q=*&page=1'}`}>
          ← Back to product search
        </Link>
      </main>
      <RelatedProducts product={product} />
      <Footer />
    </div>
  );
}

export default PublicProductDetails;
